import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faArrowLeft, 
    faMagic,
    faPlus,
    faTimes,
    faFont,
    faUpload
} from '@fortawesome/free-solid-svg-icons';
import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore";
import { API_ENDPOINTS } from '../config/apiConfig';
import { getDefaultScaleForGridSize } from './quilt/EmbossedItemRenderer';

// Import extracted components
import QuiltDisplay from './quilt/QuiltDisplay';
import EmbossedItemEditor from './quilt/EmbossedItemEditor';
import TextItemEditor from './quilt/TextItemEditor';
import QuiltActionButtons from './quilt/QuiltActionButtons';

// Main component for Step 6
const Step6 = ({
    selectedLayout, 
    prevStep, 
    currentUser,
    storage,
    db,
    setShowModal,
    setShareLink,
    aiGeneratedLayouts, // For layout switching
    onSelectLayout // For layout switching
}) => {
    // Store the original layout ID for persistence
    const [originalLayoutId, setOriginalLayoutId] = useState(null);

    // Add state for quilt dimensions from Firestore
    const [quiltDimensions, setQuiltDimensions] = useState(null);

    // Shared customization attributes (persist across layouts)
    const [sharedAttributes, setSharedAttributes] = useState({
        showBinding: true,
        bindingColor: 'white',
        customBindingColor: '',
        showBorder: false,
        borderColor: 'gray',
        customBorderColor: '',
        embossedItems: [],
        textItems: []
    });
    
    // Core quilt states - sync with shared attributes
    const [showBinding, setShowBinding] = useState(sharedAttributes.showBinding);
    const [showBorder, setShowBorder] = useState(sharedAttributes.showBorder);
    const [bindingColor, setBindingColor] = useState(sharedAttributes.bindingColor);
    const [borderColor, setBorderColor] = useState(sharedAttributes.borderColor);
    const [customBindingColor, setCustomBindingColor] = useState(sharedAttributes.customBindingColor);
    const [customBorderColor, setCustomBorderColor] = useState(sharedAttributes.customBorderColor);
    
    // Embossed items states
    const [embossedItems, setEmbossedItems] = useState(sharedAttributes.embossedItems);
    const [activeItemIndex, setActiveItemIndex] = useState(-1);
    const [showEmbossPanel, setShowEmbossPanel] = useState(false);
    
    // Text items states
    const [textItems, setTextItems] = useState(sharedAttributes.textItems);
    const [activeTextIndex, setActiveTextIndex] = useState(-1);
    const [showTextPanel, setShowTextPanel] = useState(false);
    
    // Files and refs
    const fileInputRef = useRef(null);
    const canvasRef = useRef(null);
    
    // Add a loading state for embossed image uploads
    const [embossUploading, setEmbossUploading] = useState(false);
    const [embossUploadError, setEmbossUploadError] = useState(null);

    // Extract grid dimensions from selectedLayout
    const getGridDimensionsFromLayout = () => {
        if (!selectedLayout) return { cols: 6, rows: 6 };
        
        let cols = 6, rows = 6;
        if (selectedLayout.gridSize) {
            const parts = selectedLayout.gridSize.split('x');
            if (parts.length === 2) {
                rows = parseInt(parts[0], 10) || 6;
                cols = parseInt(parts[1], 10) || 6;
            }
        }
        
        return { cols, rows };
    };
    
    // Get grid dimensions for default scale calculation
    const { cols, rows } = getGridDimensionsFromLayout();

    // Expanded predefined color options
    const colorOptions = [
        // Row 1
        { name: 'White', value: 'white' },
        { name: 'Black', value: 'black' },
        { name: 'Gray', value: 'gray' },
        { name: 'Red', value: '#c12f2d' },
        { name: 'Maroon', value: '#a52c48' },
        { name: 'Wine', value: '#872434' },
        { name: 'Dark Pink', value: '#e8396e' },
        { name: 'Magenta', value: '#dc3979' },
        // Row 2
        { name: 'Pink', value: 'pink' },
        { name: 'Baby Pink', value: '#fbb7d2' },
        { name: 'Peach', value: '#fa9fa3' },
        { name: 'Yellow', value: '#f3c400' },
        { name: 'Mint Green', value: '#d0f3c0' },
        { name: 'Green', value: '#1d5d37' },
        { name: 'Dark Green', value: '#1b3c37' },
        { name: 'Teal Green', value: '#30957b' },
        // Row 3
        { name: 'Cyan', value: '#147e99' },
        { name: 'Sky Blue', value: '#dbf3f9' },
        { name: 'Light Blue', value: '#eaf6f9' },
        { name: 'Blue', value: 'blue' },
        { name: 'Royal Blue', value: '#1a1fc1' },
        { name: 'Navy Blue', value: '#1e1a4d' },
        { name: 'Indigo', value: '#1f2b5e' },
        { name: 'Violet', value: '#902586' }
    ];
    
    // Store the original layout ID when component loads or layout changes
    useEffect(() => {
        if (selectedLayout && selectedLayout.id) {
            setOriginalLayoutId(selectedLayout.id);
        }
    }, [selectedLayout]);

    // Synchronize individual states with shared attributes when they change
    useEffect(() => {
        setShowBinding(sharedAttributes.showBinding);
        setBindingColor(sharedAttributes.bindingColor);
        setCustomBindingColor(sharedAttributes.customBindingColor);
        setShowBorder(sharedAttributes.showBorder);
        setBorderColor(sharedAttributes.borderColor);
        setCustomBorderColor(sharedAttributes.customBorderColor);
        setEmbossedItems(sharedAttributes.embossedItems);
        setTextItems(sharedAttributes.textItems);
    }, [sharedAttributes]);
    
    // Save customization to Firestore for persistence
    useEffect(() => {
        if (!currentUser || !selectedLayout) return;
        
        const saveTimeout = setTimeout(() => {
            saveCustomizationsToFirestore();
        }, 1000); // Debounce for 1 second
        
        return () => clearTimeout(saveTimeout);
    }, [sharedAttributes, currentUser, selectedLayout]);
    
    // Use a ref to track initialization status across re-renders
    const initializationAttemptedRef = useRef(false);
    const [customizationsInitialized, setCustomizationsInitialized] = useState(false);

    // Function to save customizations to Firestore
    const saveCustomizationsToFirestore = async () => {
        try {
            if (!currentUser?.uid) return;
            
            // Use only user ID for the document key - global settings for all layouts
            const customizationsRef = doc(db, "layout_customizations", currentUser.uid);
            
            // Create a data object with the shared attributes
            const customizationData = {
                updatedAt: new Date(),
                userId: currentUser.uid,
                attributes: sharedAttributes
            };
            
            // Update or create the document
            await setDoc(customizationsRef, customizationData, { merge: true });
            console.log("Saved global customizations to Firestore");
            
            // Mark as initialized
            setCustomizationsInitialized(true);
            initializationAttemptedRef.current = true;
        } catch (error) {
            console.error("Error saving customizations:", error);
            // Continue without saving - this is non-critical functionality
        }
    };
    
    // Load customizations from Firestore when component mounts
    useEffect(() => {
        const loadCustomizations = async () => {
            try {
                if (!currentUser?.uid) {
                    console.log("Missing user info - cannot load customizations");
                    return;
                }
                
                // If we've already loaded customizations, skip
                if (customizationsInitialized) {
                    return;
                }
                
                // Load global customizations for this user (not layout-specific)
                const customizationsRef = doc(db, "layout_customizations", currentUser.uid);
                
                try {
                    const customizationsDoc = await getDoc(customizationsRef);
                    
                    if (customizationsDoc.exists()) {
                        const data = customizationsDoc.data();
                        if (data.attributes) {
                            setSharedAttributes(data.attributes);
                            console.log("Loaded global customizations from Firestore");
                            setCustomizationsInitialized(true);
                        } else {
                            console.log("No attributes found in customizations document");
                            // Initialize with default values
                            await initializeCustomizationsDocument();
                        }
                    } else {
                        console.log("No customizations document exists yet - creating default");
                        // Document doesn't exist - initialize it
                        await initializeCustomizationsDocument();
                    }
                } catch (firestoreError) {
                    console.error("Error accessing layout_customizations document:", firestoreError);
                    // Fall back to using local state only
                }
            } catch (error) {
                console.error("Error in customization loading process:", error);
                // Continue without loading saved customizations
            }
        };
        
        // Function to initialize a default customizations document
        const initializeCustomizationsDocument = async () => {
            if (!currentUser?.uid) {
                console.log("Cannot initialize - missing user info");
                return;
            }
            
            try {
                const customizationsRef = doc(db, "layout_customizations", currentUser.uid);
                const defaultData = {
                    updatedAt: new Date(),
                    userId: currentUser.uid,
                    attributes: sharedAttributes // Use current default values
                };
                
                console.log("Creating default global customizations document");
                await setDoc(customizationsRef, defaultData);
                console.log("Created default customizations document successfully");
                
                setCustomizationsInitialized(true);
            } catch (error) {
                console.error("Error initializing customizations document:", error);
            }
        };
        
        // Load customizations when component mounts
        loadCustomizations();
    }, [currentUser, db, customizationsInitialized, sharedAttributes]);
    
    // Function to update shared attributes
    const updateSharedAttributes = (updates) => {
        setSharedAttributes(prev => ({
            ...prev,
            ...updates
        }));
    };
    
    // Handle binding toggle with shared attribute updates
    const handleBindingToggle = (value) => {
        setShowBinding(value);
        updateSharedAttributes({ showBinding: value });
    };
    
    // Handle border toggle with shared attribute updates
    const handleBorderToggle = (value) => {
        setShowBorder(value);
        updateSharedAttributes({ showBorder: value });
    };
    
    // Handle binding color change with shared attributes
    const handleBindingColorChange = (color) => {
        setBindingColor(color);
        setCustomBindingColor('');
        updateSharedAttributes({ 
            bindingColor: color,
            customBindingColor: '' 
        });
    };
    
    // Handle border color change with shared attributes
    const handleBorderColorChange = (color) => {
        setBorderColor(color);
        setCustomBorderColor('');
        updateSharedAttributes({ 
            borderColor: color,
            customBorderColor: '' 
        });
    };
    
    // Handle custom binding color change with shared attributes
    const handleCustomBindingColorChange = (e) => {
        setCustomBindingColor(e.target.value);
        setBindingColor('custom');
        updateSharedAttributes({ 
            bindingColor: 'custom',
            customBindingColor: e.target.value 
        });
    };
    
    // Handle custom border color change with shared attributes
    const handleCustomBorderColorChange = (e) => {
        setCustomBorderColor(e.target.value);
        setBorderColor('custom');
        updateSharedAttributes({ 
            borderColor: 'custom',
            customBorderColor: e.target.value 
        });
    };
    
    // Calculate display colors - update to handle object-based color options
    const displayBindingColor = bindingColor === 'custom' 
        ? customBindingColor 
        : colorOptions.find(c => c.value === bindingColor)?.value || bindingColor;
    
    const displayBorderColor = borderColor === 'custom' 
        ? customBorderColor 
        : colorOptions.find(c => c.value === borderColor)?.value || borderColor;
    
    // Handle file upload for embossing with background removal and proper default scale
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        try {
            // Set loading state
            setEmbossUploading(true);
            setEmbossUploadError(null);
            
            // Create form data for the API request
            const formData = new FormData();
            formData.append('image', file);
            formData.append('session_id', currentUser.uid);
            
            // Call the emboss image processing API using the centralized config
            const response = await fetch(API_ENDPOINTS.PROCESS_EMBOSS_IMAGE, {
                method: 'POST',
                body: formData,
            });
            
            if (!response.ok) {
                throw new Error(`Server returned ${response.status}: ${response.statusText}`);
            }
            
            const responseData = await response.json();
            
            if (!responseData.success || !responseData.emboss_url) {
                throw new Error(responseData.message || 'Failed to process emboss image');
            }
            
            // Create fixed base size for consistent scaling
            const baseSize = 100;
            
            // Create the embossed item with the processed image URL
            // Position at the top part of the quilt (x: 50% horizontally centered, y: 20% from the top)
            const newItem = {
                dataUrl: responseData.emboss_url,
                x: 50, // Center horizontally
                y: 20, // Position at the top area (20% from the top)
                scale: getDefaultScaleForGridSize(cols, rows), // Use appropriate default scale
                rotation: 0,
                fixedSize: baseSize, // Base size multiplier
                isUploaded: true // Flag as user uploaded
            };
            
            const updatedItems = [...embossedItems, newItem];
            setEmbossedItems(updatedItems);
            updateSharedAttributes({ embossedItems: updatedItems });
            setActiveItemIndex(updatedItems.length - 1);
            setShowEmbossPanel(true);
            
        } catch (error) {
            console.error("Error processing emboss image:", error);
            
            // Provide more helpful error messages based on error type
            if (error.message.includes('Failed to fetch')) {
                setEmbossUploadError("Cannot connect to the processing server. Please check your connection and try again.");
            } else {
                setEmbossUploadError(error.message);
            }
            
            // Create a reader for local preview fallback if API fails
            const reader = new FileReader();
            reader.onload = (event) => {
                const baseSize = 100;
                
                const newItem = {
                    dataUrl: event.target.result,
                    x: 50, // Center horizontally
                    y: 20, // Position at the top area (20% from the top)
                    rotation: 0,
                    scale: getDefaultScaleForGridSize(cols, rows), // Use appropriate default scale
                    fixedSize: baseSize,
                    zIndex: 20,
                    failedProcessing: true,
                    isUploaded: true // STILL add the flag even in fallback mode
                };
                
                const updatedItems = [...embossedItems, newItem];
                setEmbossedItems(updatedItems);
                updateSharedAttributes({ embossedItems: updatedItems });
                setActiveItemIndex(updatedItems.length - 1);
                setShowEmbossPanel(true);
            };
            reader.readAsDataURL(file);
        } finally {
            setEmbossUploading(false);
        }
    };
    
    // Sample embossed items
    const sampleEmbossedItems = [
        {
            name: "Heart",
            dataUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='24' height='24'%3E%3Cpath fill='%23ff5555' d='M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z'/%3E%3C/svg%3E"
        },
        {
            name: "Star",
            dataUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='24' height='24'%3E%3Cpath fill='%23ffdd55' d='M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z'/%3E%3C/svg%3E"
        },
        {
            name: "Flower",
            dataUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='24' height='24'%3E%3Cpath fill='%23ff99cc' d='M12,1C9.79,1,8,2.79,8,5S9.79,9,12,9s4-1.79,4-4S14.21,1,12,1z M20,13c0,2.21-1.79,4-4,4s-4-1.79-4-4s1.79-4,4-4S20,10.79,20,13z M8,13c0,2.21-1.79,4-4,4s-4-1.79-4-4s1.79-4,4-4S8,10.79,8,13z M12,23c2.21,0,4-1.79,4-4c0-2.21-1.79-4-4-4s-4,1.79-4,4C8,21.21,9.79,23,12,23z'/%3E%3C/svg%3E"
        }
    ];
    
    // Add a sample embossed item with enhanced properties
    const addSampleItem = (item) => {
        // Get the default scale based on grid size
        const defaultScale = getDefaultScaleForGridSize(cols, rows);
        
        const newItem = {
            ...item,
            x: 50, // Center horizontally
            y: 20, // Position at the top area (20% from the top)
            scale: defaultScale, // Use the default scale for this grid size
            rotation: 0,
            fixedSize: 100, // Base size multiplier
            isUploaded: false // Sample item
        };
        
        const updatedItems = [...embossedItems, newItem];
        setEmbossedItems(updatedItems);
        updateSharedAttributes({ embossedItems: updatedItems });
        setActiveItemIndex(embossedItems.length);
        setShowEmbossPanel(true);
        setShowTextPanel(false);
    };
    
    // Update existing embossed item with shared attributes update
    const updateEmbossedItem = (index, updates) => {
        // First get a copy of the current item to work with
        const currentItem = { ...embossedItems[index] };
        
        // Create a new separate object for updates to avoid reference issues
        const newUpdates = { ...updates };
        
        // IMPORTANT: Fix for size reduction issue
        // Ensure we're explicitly preserving scale when position changes
        if ((newUpdates.x !== undefined || newUpdates.y !== undefined) && newUpdates.scale === undefined) {
            // If position is changing but scale isn't explicitly being updated,
            // ensure we carry over the current scale exactly
            newUpdates.scale = currentItem.scale;
            
            // Also ensure fixed size is maintained
            if (currentItem.fixedSize) {
                newUpdates.fixedSize = currentItem.fixedSize;
            } else if (currentItem.width) {
                // If no fixed size exists but width does, establish one
                newUpdates.fixedSize = currentItem.width / (currentItem.scale || 1);
            }
        }
        
        // REMOVE ANY SCALE LIMITS HERE - Allow any scale value the slider allows
        // If scale is being updated, ensure it's within reasonable bounds
        if (newUpdates.scale !== undefined) {
            // Remove any maximum cap on scale
            // newUpdates.scale = Math.max(0.1, Math.min(3.0, newUpdates.scale)); // <-- REMOVE/MODIFY THIS LINE
            
            // Only enforce a minimum value for safety
            newUpdates.scale = Math.max(0.1, newUpdates.scale);
            
            // Ensure width is updated proportionally with scale
            if (currentItem.fixedSize) {
                newUpdates.width = currentItem.fixedSize * newUpdates.scale;
            } else {
                // If no fixed size exists, establish one based on current width and scale
                const baseSize = (currentItem.width || 100) / (currentItem.scale || 1);
                newUpdates.fixedSize = baseSize;
                newUpdates.width = baseSize * newUpdates.scale;
            }
        }
        
        // Create the final updated item by combining current item with sanitized updates
        const updatedItem = { ...currentItem, ...newUpdates };
        
        // Add another safeguard to ensure width and scale are synchronized
        if (updatedItem.fixedSize && updatedItem.scale) {
            updatedItem.width = updatedItem.fixedSize * updatedItem.scale;
        }
        
        // Debug logging
        console.log(`Updating embossed item ${index}:`, {
            before: {
                x: currentItem.x,
                y: currentItem.y,
                scale: currentItem.scale,
                width: currentItem.width,
                fixedSize: currentItem.fixedSize
            },
            after: {
                x: updatedItem.x,
                y: updatedItem.y,
                scale: updatedItem.scale,
                width: updatedItem.width,
                fixedSize: updatedItem.fixedSize
            }
        });
        
        // Update the state with our protected values
        const updatedItems = [...embossedItems];
        updatedItems[index] = updatedItem;
        setEmbossedItems(updatedItems);
        updateSharedAttributes({ embossedItems: updatedItems });
    };
    
    // Delete embossed item with shared attributes update
    const deleteEmbossedItem = (index) => {
        const updatedItems = embossedItems.filter((_, i) => i !== index);
        setEmbossedItems(updatedItems);
        updateSharedAttributes({ embossedItems: updatedItems });
        
        // Update active item index if needed
        if (activeItemIndex === index) {
            setActiveItemIndex(updatedItems.length > 0 ? 0 : -1);
        } else if (activeItemIndex > index) {
            setActiveItemIndex(activeItemIndex - 1);
        }
    };
    
    // Add a new text item with shared attributes update
    const addTextItem = () => {
        const newItem = {
            text: "Your Text Here",
            fontFamily: "Arial",
            fontSize: 24,
            color: "#000000",
            backgroundColor: "#FFFFFF", // Default white background
            x: 50, // center of quilt horizontally
            y: 50, // center of quilt vertically
            rotation: 0,
            bold: false,
            italic: false,
            underline: false,
            showBorder: true, // Enable border by default
            borderColor: "#000000" // Default black border color
        };
        
        updateSharedAttributes({
            textItems: [...textItems, newItem]
        });
        
        // Focus the newly added text item
        setActiveTextIndex(textItems.length);
        
        // Scroll to the new item after a small delay to ensure rendering
        setTimeout(() => {
            const newItemElement = document.getElementById(`text-item-${textItems.length}`);
            if (newItemElement) {
                newItemElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
    };
    
    // Update existing text item with shared attributes update
    const updateTextItem = (index, updates) => {
        const updatedItems = [...textItems];
        updatedItems[index] = { ...updatedItems[index], ...updates };
        setTextItems(updatedItems);
        updateSharedAttributes({ textItems: updatedItems });
    };
    
    // Delete text item with shared attributes update
    const deleteTextItem = (index) => {
        const updatedItems = textItems.filter((_, i) => i !== index);
        setTextItems(updatedItems);
        updateSharedAttributes({ textItems: updatedItems });
        
        // Update active text index if needed
        if (activeTextIndex === index) {
            setActiveTextIndex(updatedItems.length > 0 ? 0 : -1);
        } else if (activeTextIndex > index) {
            setActiveTextIndex(activeTextIndex - 1);
        }
    };
    
    // Function to switch to a different layout while maintaining customizations
    const switchToLayout = (layoutId) => {
        // Find the layout in the aiGeneratedLayouts array
        const newLayout = aiGeneratedLayouts.find(layout => layout.id === layoutId);
        
        if (newLayout) {
            // Call the parent component's layout selection function
            onSelectLayout(newLayout);
        }
    };
    
    // Add a helper function to prepare embossed items for rendering
    const prepareEmbossedItemsForRender = (items) => {
        if (!items || !Array.isArray(items)) return [];
        
        // Make a deep copy to avoid modifying the original
        const preparedItems = JSON.parse(JSON.stringify(items));
        
        // Get grid dimensions
        let gridDimensions = { cols: 6, rows: 6 };
        if (selectedLayout && selectedLayout.gridSize) {
            const parts = selectedLayout.gridSize.split('x');
            if (parts.length === 2) {
                gridDimensions.cols = parseInt(parts[0], 10) || 6;
                gridDimensions.rows = parseInt(parts[1], 10) || 6;
            }
        }
        
        // Adjust scale based on grid size for each item
        return preparedItems.map(item => {
            // Ensure each item has minimum required properties
            const baseItem = {
                ...item,
                x: item.x !== undefined ? item.x : 50,
                y: item.y !== undefined ? item.y : 50,
                scale: item.scale !== undefined ? item.scale : 1,
                rotation: item.rotation || 0,
            };
            
            return baseItem;
        });
    };
    
    // Pass the prepared items to QuiltDisplay
    const preparedEmbossedItems = prepareEmbossedItemsForRender(embossedItems);
    
    // Enhanced layout selector with persistence
    const renderLayoutSelector = () => {
        if (!aiGeneratedLayouts || aiGeneratedLayouts.length <= 1) {
            return null;
        }
        
        return (
            <div className="mb-6 bg-gray-50 p-3 rounded-lg border border-gray-200">
                <h3 className="text-sm font-medium mb-2">Switch Layout:</h3>
                <div className="flex flex-wrap gap-2">
                    {aiGeneratedLayouts.map((layout, index) => (
                        <button
                            key={layout.id || index}
                            onClick={() => switchToLayout(layout.id)}
                            className={`text-xs py-1 px-3 rounded ${
                                selectedLayout.id === layout.id 
                                ? 'bg-[#9CAF88] text-white' 
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                            Layout {index + 1}
                        </button>
                    ))}
                </div>
                <div className="text-xs text-gray-500 mt-2">
                    Your customizations will be preserved when switching layouts.
                </div>
            </div>
        );
    };
    
    // When receiving the selected layout, ensure we load the quilt grid data
    useEffect(() => {
        if (selectedLayout) {
            console.log("Selected layout for customization:", selectedLayout);
            
            // Try to load quilt grid from session storage if not in the layout
            if (!selectedLayout.quiltGrid && sessionStorage.getItem('quiltGridData')) {
                try {
                    const quiltGridData = JSON.parse(sessionStorage.getItem('quiltGridData'));
                    if (Array.isArray(quiltGridData) && quiltGridData.length > 0) {
                        // Attach the quilt grid to the selected layout for rendering
                        selectedLayout.quiltGrid = quiltGridData;
                        console.log("Loaded quilt grid from session storage:", quiltGridData.length);
                    }
                } catch (error) {
                    console.error("Error parsing quilt grid from session storage:", error);
                }
            }
        }
    }, [selectedLayout]);

    // Calculate canvas dimensions for embossed item scaling
    const [canvasDimensions, setCanvasDimensions] = useState({ 
        width: 800, height: 800, gridCols: 6, gridRows: 6 
    });
    
    // Update canvas dimensions based on selected layout
    useEffect(() => {
        if (selectedLayout) {
            // Extract grid dimensions
            let cols = 6, rows = 6; // Default dimensions
            if (selectedLayout.gridSize) {
                const dimensions = selectedLayout.gridSize.split('x').map(Number);
                cols = dimensions[0] || 6;
                rows = dimensions[1] || 6;
            } else if (selectedLayout.original_grid_size) {
                const dimensions = selectedLayout.original_grid_size.split('x').map(Number);
                cols = dimensions[0] || 6;
                rows = dimensions[1] || 6;
            }
            
            // Get actual tile dimensions from quiltDimensions
            let baseTileWidth = 100;
            let baseTileHeight = 100;
            
            if (quiltDimensions && quiltDimensions.tileWidthPixels && quiltDimensions.tileHeightPixels) {
                baseTileWidth = quiltDimensions.tileWidthPixels;
                baseTileHeight = quiltDimensions.tileHeightPixels;
            }
            
            // Calculate content area size
            const contentWidth = cols * baseTileWidth;
            const contentHeight = rows * baseTileHeight;
            
            // Add binding and border sizes using the same calculation as CanvasQuiltRenderer
            // DECREASED VALUES BY ANOTHER 20% to match CanvasQuiltRenderer
            const bindingSize = showBinding ? Math.max(Math.round(contentWidth * 0.0235008), 13) : 0; // DECREASED
            const borderSize = showBorder ? Math.max(Math.round(contentWidth * 0.039168), 18) : 0;   // DECREASED
            
            // Calculate final canvas dimensions with equal margins
            const totalHorizontalMargin = 2 * (bindingSize + borderSize);
            const totalVerticalMargin = 2 * (bindingSize + borderSize);
            
            const finalWidth = contentWidth + totalHorizontalMargin;
            const finalHeight = contentHeight + totalVerticalMargin;
            
            console.log(`Canvas dimensions: ${finalWidth}x${finalHeight}, Content: ${contentWidth}x${contentHeight}, Binding: ${bindingSize}px, Border: ${borderSize}px`);
            
            setCanvasDimensions({
                width: finalWidth,
                height: finalHeight,
                gridCols: cols,
                gridRows: rows
            });
        }
    }, [selectedLayout, showBinding, showBorder, quiltDimensions]);
    
    // Extract grid size from selected layout to pass to embossed item editor
    const getLayoutGridSize = () => {
        if (!selectedLayout) return null;
        
        // Try to get grid size from different possible properties
        return selectedLayout.gridSize || 
               selectedLayout.original_grid_size || 
               (selectedLayout.tileData && 
                Array.isArray(selectedLayout.tileData) && 
                selectedLayout.tileData.length > 0 && 
                `${Math.max(...selectedLayout.tileData.map(t => t.col || 0)) + 1}x${Math.max(...selectedLayout.tileData.map(t => t.row || 0)) + 1}`);
    };

    const layoutGridSize = getLayoutGridSize();
    
    // Update renderKey whenever colors change to force fresh rendering
    const [renderKey, setRenderKey] = useState(Date.now());
    
    useEffect(() => {
        // Generate a new renderKey when colors or border/binding settings change
        setRenderKey(Date.now());
    }, [showBinding, showBorder, displayBindingColor, displayBorderColor, embossedItems, textItems]);
    
    // Load quilt dimensions from Firestore when component mounts
    useEffect(() => {
        const loadQuiltDimensions = async () => {
            if (!currentUser?.uid || !db) return;
            
            try {
                // Try to get dimensions from the sessions collection
                const sessionRef = doc(db, 'sessions', currentUser.uid);
                const sessionSnap = await getDoc(sessionRef);
                
                if (sessionSnap.exists()) {
                    const sessionData = sessionSnap.data();
                    
                    if (sessionData.quiltDimensions) {
                        console.log('Loaded quilt dimensions from Firestore:', sessionData.quiltDimensions);
                        setQuiltDimensions(sessionData.quiltDimensions);
                    }
                }
            } catch (error) {
                console.error('Error loading quilt dimensions:', error);
            }
        };
        
        loadQuiltDimensions();
    }, [currentUser, db]);
    
    return (
        <div className="bg-white rounded-lg p-3 sm:p-6 shadow-md">
            <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#9CAF88]" style={{ fontFamily: "Playfair Display" }}>
                <FontAwesomeIcon icon={faMagic} className="mr-2" />
                Customize Your Memory Quilt
            </h2>
            
            <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
                Enhance your quilt with a 1-inch binding, 2-inch border, embossed elements, and personalized text.
            </p>
            
            {/* Layout Selector */}
            {renderLayoutSelector()}
            
            {/* Main Layout: Grid and Controls */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Quilt Preview with Binding and Border */}
                <div className="md:col-span-2">
                    {/* Remove any margin/padding from this container */}
                    <div className="relative mb-6" style={{ margin: 0, padding: 0 }}>
                        <QuiltDisplay 
                            key={`quilt-${renderKey}`} // Force re-render when colors change
                            selectedLayout={selectedLayout}
                            showBinding={showBinding}
                            showBorder={showBorder}
                            bindingColor={displayBindingColor}
                            borderColor={displayBorderColor}
                            embossedItems={preparedEmbossedItems} // Use the prepared items
                            textItems={textItems}
                            canvasRef={canvasRef}
                            firestoreDimensions={quiltDimensions} // Pass the actual dimensions from Firestore
                        />
                    </div>
                    
                    {/* Action Buttons - Update to pass firestoreDimensions */}
                    <QuiltActionButtons
                        canvasRef={canvasRef}
                        showBinding={showBinding}
                        showBorder={showBorder}
                        bindingColor={displayBindingColor}
                        borderColor={displayBorderColor}
                        embossedItems={preparedEmbossedItems}
                        textItems={textItems}
                        currentUser={currentUser}
                        storage={storage}
                        db={db}
                        setShowModal={setShowModal}
                        setShareLink={setShareLink}
                        firestoreDimensions={quiltDimensions} // Pass the dimensions here too for downloads
                    />
                </div>
                
                {/* Right Sidebar - Controls */}
                <div className="md:col-span-1">
                    <div className="border border-gray-300 rounded-md overflow-hidden">
                        {/* Tabs */}
                        <div className="flex border-b border-gray-300">
                            <button 
                                className={`flex-1 py-2 text-center font-medium text-sm ${!showEmbossPanel && !showTextPanel ? 'bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-600'}`}
                                onClick={() => {
                                    setShowEmbossPanel(false);
                                    setShowTextPanel(false);
                                }}
                            >
                                Border
                            </button>
                            <button 
                                className={`flex-1 py-2 text-center font-medium text-sm ${showEmbossPanel ? 'bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-600'}`}
                                onClick={() => {
                                    setShowEmbossPanel(true);
                                    setShowTextPanel(false);
                                }}
                            >
                                Emboss
                            </button>
                            <button 
                                className={`flex-1 py-2 text-center font-medium text-sm ${showTextPanel ? 'bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-600'}`}
                                onClick={() => {
                                    setShowTextPanel(true);
                                    setShowEmbossPanel(false);
                                }}
                            >
                                Text
                            </button>
                        </div>
                        
                        {/* Tab Content */}
                        <div className="p-3 max-h-[500px] overflow-y-auto">
                            {!showEmbossPanel && !showTextPanel ? (
                                // Border Settings Tab
                                <div>
                                    {/* Binding Options */}
                                    <div className="mb-4 border-b border-gray-200 pb-4">
                                        <div className="flex items-center mb-3">
                                            <input
                                                id="binding-toggle"
                                                type="checkbox"
                                                checked={showBinding}
                                                onChange={() => handleBindingToggle(!showBinding)}
                                                className="h-4 w-4 text-blue-600 rounded"
                                            />
                                            <label htmlFor="binding-toggle" className="ml-2 font-medium text-sm">
                                                Add Binding (1.55-inch outer edge)
                                            </label>
                                        </div>
                                        
                                        {showBinding && (
                                            <div className="ml-6">
                                                <p className="mb-2 text-xs">Binding Color:</p>
                                                {/* Row 1 */}
                                                <div className="grid grid-cols-8 gap-1 mb-1">
                                                    {colorOptions.slice(0, 8).map((color) => (
                                                        <button
                                                            key={`binding-${color.name}`}
                                                            onClick={() => handleBindingColorChange(color.value)}
                                                            className={`h-6 rounded-md border ${
                                                                bindingColor === color.value 
                                                                ? 'ring-2 ring-blue-500' 
                                                                : 'border-gray-300'
                                                            }`}
                                                            style={{ backgroundColor: color.value }}
                                                            aria-label={`${color.name} binding`}
                                                            title={color.name}
                                                        >
                                                            {color.name === 'White' ? <span className="text-xs text-gray-500">W</span> : null}
                                                        </button>
                                                    ))}
                                                </div>
                                                {/* Row 2 */}
                                                <div className="grid grid-cols-8 gap-1 mb-1">
                                                    {colorOptions.slice(8, 16).map((color) => (
                                                        <button
                                                            key={`binding-${color.name}`}
                                                            onClick={() => handleBindingColorChange(color.value)}
                                                            className={`h-6 rounded-md border ${
                                                                bindingColor === color.value 
                                                                ? 'ring-2 ring-blue-500' 
                                                                : 'border-gray-300'
                                                            }`}
                                                            style={{ backgroundColor: color.value }}
                                                            aria-label={`${color.name} binding`}
                                                            title={color.name}
                                                        />
                                                    ))}
                                                </div>
                                                {/* Row 3 */}
                                                <div className="grid grid-cols-8 gap-1 mb-3">
                                                    {colorOptions.slice(16, 24).map((color) => (
                                                        <button
                                                            key={`binding-${color.name}`}
                                                            onClick={() => handleBindingColorChange(color.value)}
                                                            className={`h-6 rounded-md border ${
                                                                bindingColor === color.value 
                                                                ? 'ring-2 ring-blue-500' 
                                                                : 'border-gray-300'
                                                            }`}
                                                            style={{ backgroundColor: color.value }}
                                                            aria-label={`${color.name} binding`}
                                                            title={color.name}
                                                        />
                                                    ))}
                                                </div>
                                                
                                                <button
                                                    onClick={() => handleBindingColorChange('custom')}
                                                    className={`h-6 w-full rounded-md border mb-2 ${
                                                        bindingColor === 'custom' 
                                                        ? 'ring-2 ring-blue-500' 
                                                        : 'border-gray-300'
                                                    }`}
                                                    style={{ background: 'linear-gradient(to right, red, yellow, green, blue, purple)' }}
                                                    aria-label="Custom color binding"
                                                >
                                                    <span className="text-xs text-white font-bold drop-shadow">Custom</span>
                                                </button>
                                                
                                                {bindingColor === 'custom' ? (
                                                    <input
                                                        type="text"
                                                        value={customBindingColor}
                                                        onChange={handleCustomBindingColorChange}
                                                        placeholder="Color name or hex (#ff0000)"
                                                        className="w-full p-1 text-sm border border-gray-300 rounded-md"
                                                    />
                                                ) : null}
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Border Options - Same structure as binding but for border */}
                                    <div className="mb-4">
                                        <div className="flex items-center mb-3">
                                            <input
                                                id="border-toggle"
                                                type="checkbox"
                                                checked={showBorder}
                                                onChange={() => handleBorderToggle(!showBorder)}
                                                className="h-4 w-4 text-blue-600 rounded"
                                            />
                                            <label htmlFor="border-toggle" className="ml-2 font-medium text-sm">
                                                Add Border (3.11-inch inner frame)
                                            </label>
                                        </div>
                                        
                                        {showBorder && (
                                            <div className="ml-6">
                                                <p className="mb-2 text-xs">Border Color:</p>
                                                {/* Row 1 */}
                                                <div className="grid grid-cols-8 gap-1 mb-1">
                                                    {colorOptions.slice(0, 8).map((color) => (
                                                        <button
                                                            key={`border-${color.name}`}
                                                            onClick={() => handleBorderColorChange(color.value)}
                                                            className={`h-6 rounded-md border ${
                                                                borderColor === color.value 
                                                                ? 'ring-2 ring-blue-500' 
                                                                : 'border-gray-300'
                                                            }`}
                                                            style={{ backgroundColor: color.value }}
                                                            aria-label={`${color.name} border`}
                                                            title={color.name}
                                                        >
                                                            {color.name === 'White' ? <span className="text-xs text-gray-500">W</span> : null}
                                                        </button>
                                                    ))}
                                                </div>
                                                {/* Row 2 */}
                                                <div className="grid grid-cols-8 gap-1 mb-1">
                                                    {colorOptions.slice(8, 16).map((color) => (
                                                        <button
                                                            key={`border-${color.name}`}
                                                            onClick={() => handleBorderColorChange(color.value)}
                                                            className={`h-6 rounded-md border ${
                                                                borderColor === color.value 
                                                                ? 'ring-2 ring-blue-500' 
                                                                : 'border-gray-300'
                                                            }`}
                                                            style={{ backgroundColor: color.value }}
                                                            aria-label={`${color.name} border`}
                                                            title={color.name}
                                                        />
                                                    ))}
                                                </div>
                                                {/* Row 3 */}
                                                <div className="grid grid-cols-8 gap-1 mb-3">
                                                    {colorOptions.slice(16, 24).map((color) => (
                                                        <button
                                                            key={`border-${color.name}`}
                                                            onClick={() => handleBorderColorChange(color.value)}
                                                            className={`h-6 rounded-md border ${
                                                                borderColor === color.value 
                                                                ? 'ring-2 ring-blue-500' 
                                                                : 'border-gray-300'
                                                            }`}
                                                            style={{ backgroundColor: color.value }}
                                                            aria-label={`${color.name} border`}
                                                            title={color.name}
                                                        />
                                                    ))}
                                                </div>
                                                
                                                <button
                                                    onClick={() => handleBorderColorChange('custom')}
                                                    className={`h-6 w-full rounded-md border mb-2 ${
                                                        borderColor === 'custom' 
                                                        ? 'ring-2 ring-blue-500' 
                                                        : 'border-gray-300'
                                                    }`}
                                                    style={{ background: 'linear-gradient(to right, red, yellow, green, blue, purple)' }}
                                                    aria-label="Custom color border"
                                                >
                                                    <span className="text-xs text-white font-bold drop-shadow">Custom</span>
                                                </button>
                                                
                                                {borderColor === 'custom' && (
                                                    <input
                                                        type="text"
                                                        value={customBorderColor}
                                                        onChange={handleCustomBorderColorChange}
                                                        placeholder="Color name or hex (#ff0000)"
                                                        className="w-full p-1 text-sm border border-gray-300 rounded-md"
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : showEmbossPanel ? (
                                // Emboss Settings Tab
                                <div>
                                    <h3 className="font-medium mb-3 text-sm">Add Embossed Items</h3>
                                    
                                    {/* Upload Button */}
                                    <div className="mb-4">
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileUpload}
                                            accept="image/*"
                                            className="hidden"
                                            disabled={embossUploading}
                                        />
                                        <button
                                            onClick={() => fileInputRef.current.click()}
                                            className={`w-full ${embossUploading ? 'bg-gray-400' : 'bg-[#9CAF88] hover:bg-[#8A9B79]'} text-white font-medium py-2 px-4 rounded-md flex items-center justify-center text-sm`}
                                            disabled={embossUploading}
                                        >
                                            {embossUploading ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Processing Image...
                                                </>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon={faUpload} className="mr-2" />
                                                    Upload Image
                                                </>
                                            )}
                                        </button>
                                        <p className="text-xs text-gray-500 mt-1 text-center">
                                            Backgrounds will be automatically removed from uploaded images
                                        </p>
                                        
                                        {/* Display error message if there was an error processing */}
                                        {embossUploadError && (
                                            <div className="mt-2 text-xs text-red-500 bg-red-50 p-2 rounded border border-red-200">
                                                Error: {embossUploadError}. Using original image instead.
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Sample Items */}
                                    <div className="mb-4">
                                        <h4 className="text-xs font-medium mb-2">Sample Items:</h4>
                                        <div className="grid grid-cols-3 gap-2">
                                            {sampleEmbossedItems.map((item, index) => (
                                                <div 
                                                    key={index}
                                                    className="border border-gray-300 rounded p-1 cursor-pointer hover:border-blue-500"
                                                    onClick={() => addSampleItem(item)}
                                                >
                                                    <div className="aspect-square bg-gray-50 flex items-center justify-center">
                                                        <img 
                                                            src={item.dataUrl} 
                                                            alt={item.name} 
                                                            className="max-h-full max-w-full object-contain"
                                                        />
                                                    </div>
                                                    <p className="text-xs text-center mt-1">{item.name}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Item Editor */}
                                    {embossedItems.length > 0 && (
                                        <div className="mt-4">
                                            <h4 className="text-xs font-medium mb-2">Your Items:</h4>
                                            <div className="max-h-64 overflow-y-auto">
                                                {embossedItems.map((item, index) => (
                                                    <EmbossedItemEditor
                                                        key={index}
                                                        item={item}
                                                        index={index}
                                                        updateItem={updateEmbossedItem}
                                                        deleteItem={deleteEmbossedItem}
                                                        setActiveItem={setActiveItemIndex}
                                                        isActive={index === activeItemIndex}
                                                        gridSize={layoutGridSize} // Pass the extracted grid size here
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                // Text Panel
                                <div>
                                    <h3 className="font-medium mb-3 text-sm">Add Text Elements</h3>

                                    {/* Add Text Button */}
                                    <div className="mb-4">
                                        <button
                                            onClick={addTextItem}
                                            className="w-full bg-[#9CAF88] hover:bg-[#8A9B79] text-white font-medium py-2 px-4 rounded-md flex items-center justify-center text-sm"
                                        >
                                            <FontAwesomeIcon icon={faFont} className="mr-2" />
                                            Add Text
                                        </button>
                                    </div>

                                    {/* Text Items Editor */}
                                    {textItems.length > 0 && (
                                        <div className="mt-4">
                                            <h4 className="text-xs font-medium mb-2">Your Text Items:</h4>
                                            <div className="max-h-64 overflow-y-auto">
                                                {textItems.map((item, index) => (
                                                    <TextItemEditor
                                                        key={index}
                                                        item={item}
                                                        index={index}
                                                        updateItem={updateTextItem}
                                                        deleteItem={deleteTextItem}
                                                        setActiveItem={setActiveTextIndex}
                                                        isActive={index === activeTextIndex}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {/* Navigation buttons */}
            <div className="flex justify-between mt-4 sm:mt-6">
                <button
                    onClick={prevStep}
                    className="mt-4 w-full py-2 bg-[#B8A99A] hover:bg-[#A69688] text-white rounded-lg flex items-center justify-center"
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Back to AI Layouts
                </button>
            </div>
        </div>
    );
};

export default Step6;
